/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-23
 */

import { ReactNode, JSX } from 'react';
import { Box, Typography, Alert } from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';

// Add local dependencies.
import { useStudioPermissions } from '../../hooks/useStudioPermissions';
import { StudioPermissions, StudioRole } from '../../types/studio';
import { hasRoleLevel } from '../../utils/permissions';

export interface PermissionGateProps {
  // Permission-based access control
  permission?: keyof StudioPermissions;
  permissions?: (keyof StudioPermissions)[];
  requireAll?: boolean; // If true, requires ALL permissions; if false, requires ANY

  // Role-based access control
  role?: StudioRole;
  roles?: StudioRole[];
  requireMinimumRole?: boolean; // If true, checks role hierarchy

  // Custom permission check function
  customCheck?: (
    permissions: StudioPermissions,
    role: StudioRole | null
  ) => boolean;

  // Content
  children: ReactNode;
  fallback?: ReactNode;
  showAccessDenied?: boolean;
  accessDeniedMessage?: string;

  // Loading state
  showLoadingState?: boolean;
  loadingComponent?: ReactNode;
}

/**
 * Component that conditionally renders children based on user permissions
 * @example
 * <PermissionGate permission="manageUsers">
 *   <UserManagementPanel />
 * </PermissionGate>
 * @returns JSX.Element | null - The children if access is granted, fallback content, or null
 */
export const PermissionGate = ({
  permission,
  permissions,
  requireAll = true,
  role,
  roles,
  requireMinimumRole = false,
  customCheck,
  children,
  fallback,
  showAccessDenied = false,
  accessDeniedMessage = 'You do not have permission to access this feature.',
  showLoadingState = true,
  loadingComponent,
}: PermissionGateProps): JSX.Element | null => {
  const {
    permissions: userPermissions,
    role: userRole,
    hasPermission,
    isLoading,
    error,
  } = useStudioPermissions();

  // Show loading state if permissions are being fetched
  if (isLoading && showLoadingState) {
    if (loadingComponent) {
      return loadingComponent as JSX.Element;
    }
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Loading permissions...
        </Typography>
      </Box>
    );
  }

  // Show error state if there's an error fetching permissions
  if (error) {
    return (
      <Alert severity="error" sx={{ m: 1 }}>
        Error loading permissions. Please try again.
      </Alert>
    );
  }

  // Check permissions
  let hasAccess = true;

  // Custom permission check takes precedence
  if (customCheck) {
    hasAccess = customCheck(userPermissions, userRole);
  } else {
    // Check specific permission
    if (permission) {
      hasAccess = hasPermission(permission);
    }

    // Check multiple permissions
    if (permissions && permissions.length > 0) {
      if (requireAll) {
        // Require ALL permissions
        hasAccess = permissions.every(perm => hasPermission(perm));
      } else {
        // Require ANY permission
        hasAccess = permissions.some(perm => hasPermission(perm));
      }
    }

    // Check role-based access
    if (role) {
      if (requireMinimumRole) {
        // Check if user has minimum role level
        hasAccess = hasAccess && hasRoleLevel(userRole, role);
      } else {
        // Check exact role match
        hasAccess = hasAccess && userRole === role;
      }
    }

    // Check multiple roles
    if (roles && roles.length > 0) {
      if (requireMinimumRole) {
        // Check if user has any of the minimum role levels
        hasAccess = hasAccess && roles.some(r => hasRoleLevel(userRole, r));
      } else {
        // Check if user has any of the exact roles
        hasAccess = hasAccess && (userRole ? roles.includes(userRole) : false);
      }
    }
  }

  // Render based on access
  if (hasAccess) {
    return children as JSX.Element;
  }

  // Show custom fallback if provided
  if (fallback) {
    return fallback as JSX.Element;
  }

  // Show access denied message if requested
  if (showAccessDenied) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          textAlign: 'center',
          color: 'text.secondary',
        }}
      >
        <LockIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
        <Typography variant="h6" gutterBottom>
          Access Restricted
        </Typography>
        <Typography variant="body2" sx={{ maxWidth: 400 }}>
          {accessDeniedMessage}
        </Typography>
      </Box>
    );
  }

  // Default: render nothing
  return null;
};

/**
 * Simplified permission gate for single permission checks
 * @example
 * <RequirePermission permission="manageUsers">
 *   <UserManagementPanel />
 * </RequirePermission>
 * @returns ReactNode - The children if access is granted, fallback content, or null
 */
export const RequirePermission = ({
  permission,
  children,
  fallback,
  showAccessDenied = false,
}: {
  permission: keyof StudioPermissions;
  children: ReactNode;
  fallback?: ReactNode;
  showAccessDenied?: boolean;
}) => (
  <PermissionGate
    permission={permission}
    fallback={fallback}
    showAccessDenied={showAccessDenied}
  >
    {children}
  </PermissionGate>
);

/**
 * Simplified permission gate for role checks
 * @example
 * <RequireRole role={StudioRole.ADMIN}>
 *   <AdminPanel />
 * </RequireRole>
 * @returns ReactNode - The children if access is granted, fallback content, or null
 */
export const RequireRole = ({
  role,
  requireMinimum = false,
  children,
  fallback,
  showAccessDenied = false,
}: {
  role: StudioRole;
  requireMinimum?: boolean;
  children: ReactNode;
  fallback?: ReactNode;
  showAccessDenied?: boolean;
}) => (
  <PermissionGate
    role={role}
    requireMinimumRole={requireMinimum}
    fallback={fallback}
    showAccessDenied={showAccessDenied}
  >
    {children}
  </PermissionGate>
);

/**
 * Permission gate for owner-only features
 * @example
 * <OwnerOnly>
 *   <StudioSettings />
 * </OwnerOnly>
 * @returns ReactNode - The children if access is granted, fallback content, or null
 */
export const OwnerOnly = ({
  children,
  fallback,
  showAccessDenied = false,
}: {
  children: ReactNode;
  fallback?: ReactNode;
  showAccessDenied?: boolean;
}) => (
  <RequireRole
    role={StudioRole.OWNER}
    fallback={fallback}
    showAccessDenied={showAccessDenied}
  >
    {children}
  </RequireRole>
);

/**
 * Permission gate for manager+ features
 * @example
 * <ManagerOrAbove>
 *   <ManagerDashboard />
 * </ManagerOrAbove>
 * @returns ReactNode - The children if access is granted, fallback content, or null
 */
export const ManagerOrAbove = ({
  children,
  fallback,
  showAccessDenied = false,
}: {
  children: ReactNode;
  fallback?: ReactNode;
  showAccessDenied?: boolean;
}) => (
  <RequireRole
    role={StudioRole.MANAGER}
    requireMinimum
    fallback={fallback}
    showAccessDenied={showAccessDenied}
  >
    {children}
  </RequireRole>
);

/**
 * Permission gate for admin+ features
 * @example
 * <AdminOrAbove>
 *   <AdminPanel />
 * </AdminOrAbove>
 * @returns ReactNode - The children if access is granted, fallback content, or null
 */
export const AdminOrAbove = ({
  children,
  fallback,
  showAccessDenied = false,
}: {
  children: ReactNode;
  fallback?: ReactNode;
  showAccessDenied?: boolean;
}) => (
  <RequireRole
    role={StudioRole.ADMIN}
    requireMinimum
    fallback={fallback}
    showAccessDenied={showAccessDenied}
  >
    {children}
  </RequireRole>
);
